import React, { memo, useCallback } from 'react';
import { TableRow, TableCell, Checkbox, Typography, Avatar, Box, Chip, Stack, Tooltip, useTheme } from '@mui/material';
import { Product } from '@domain/product/models/Product';
import Label from '@shared/ui/common/Label';
import JSEditableInput from '@shared/ui/common/JSEditableInput';
import LeadTimeDisplay from './LeadTimeDisplay';
import { ProductMode } from '@domain/product/models/ProductMode';
import { APP_CONFIG } from '../../../../config/app.config';
import { useNavigate } from 'react-router-dom';
import { useProductUpdate } from '../../hooks/useProductUpdate';
import { IconSize, Icon } from '@shared/icons';

interface ProductRowProps {
  product: Product;
  onSelect?: (product: Product, selected: boolean) => void;
  isSelected?: boolean;
  onLeadTimeClick?: (product: Product, event: React.MouseEvent<HTMLDivElement>) => void;
}

const ProductRow: React.FC<ProductRowProps> = ({ product, isSelected, onSelect, onLeadTimeClick }) => {

  const theme = useTheme();

  const { updateProduct } = useProductUpdate();
  const navigate = useNavigate();
  const modeOptions = [
    { value: ProductMode.REVENUE_OPTIMIZED, label: 'Revenue optimized', color: APP_CONFIG.ui.theme.colors.success, icon: '/assets/icons/ic_importance_high.svg' },
    { value: ProductMode.BALANCED, label: 'Balanced', color: APP_CONFIG.ui.theme.colors.primary, icon: '/assets/icons/ic_importance_medium.svg' },
    { value: ProductMode.COST_OPTIMIZED, label: 'Cost optimized', color: APP_CONFIG.ui.theme.colors.error, icon: '/assets/icons/ic_importance_low.svg' },
  ];

  const handleModeChange = useCallback((value: string) => {
    updateProduct({
      productId: product.id,
      updateData: {
        mode: value === 'not_set' ? undefined : value as ProductMode
      }
    });
  }, [product, updateProduct]);

  const handleProductClick = useCallback((product: Product) => {
    navigate(`/products/${product.id}`);
  }, [navigate]);

  const handleSelect = useCallback((selected: boolean) => {
    onSelect?.(product, selected);
  }, [onSelect, product]);

  return (
    <TableRow
      hover
      onClick={() => handleProductClick(product)}
      sx={{
        cursor: 'pointer',
        '& td': {
          borderBottom: 1,
          borderColor: 'divider',
          py: 2 // Add some vertical padding
        }
      }}>

      <TableCell padding="checkbox" sx={{ paddingLeft: 3 }}>
        <Checkbox
          checked={isSelected}
          onChange={(e) => handleSelect(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      </TableCell>

      <TableCell>
        <Stack direction="row" alignItems="center" gap={2} p={1}>
          <Avatar src={product.image} sx={{ width: 40, height: 40, border: 1, borderColor: APP_CONFIG.ui.theme.colors.border }} variant="rounded" />

          <Typography variant="body2" sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexGrow: 1,
          }}>
            {product.title}
          </Typography>

          {(product.listings?.length === 0 || product.listings?.every(listing => !listing.active)) && (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <Tooltip title="No active listings" arrow>
                <span>
                  <Icon icon="AlertTriangle" size={IconSize.SMALL} color={theme.palette.error.main} />
                </span>
              </Tooltip>
            </span>
          )}
     

          {product.usedIn && product.usedIn.length > 1 && (
            <Chip label={`Used in ${product.usedIn.length} products`} color="secondary" size="small" />
          )}

          {product.parts && product.parts.length === 1 && (
            <Chip label={`Bundle`} color="info" size="small" />
          )}

          {product.parts && product.parts.length > 1 && (
            <Chip label={`Multipack`} color="info" size="small" />
          )}

        </Stack>
      </TableCell>
      <TableCell>
        <LeadTimeDisplay
          product={product}
          onClick={(event) => {
            event.stopPropagation();
            onLeadTimeClick?.(product, event);
          }}
        />
      </TableCell>
      <TableCell>
        <JSEditableInput
          sx={{ width: '120px' }}
          value={product.minimumOrderAmountDays ? product.minimumOrderAmountDays / 7 : undefined}
          onValueChange={(value) => updateProduct({ productId: product.id, updateData: { minimumOrderAmountDays: value ? Number(value) * 7 : undefined } })}
          placeholder="0"
          suffix="weeks"

        />
      </TableCell>
      <TableCell>
        <JSEditableInput
          sx={{ width: '120px' }}
          value={product.minimumDaysOfStockInReserve ? product.minimumDaysOfStockInReserve / 7 : undefined}
          onValueChange={(value) => updateProduct({ productId: product.id, updateData: { minimumDaysOfStockInReserve: value ? Number(value) * 7 : undefined } })}
          placeholder="0"
          suffix="weeks"
        />
      </TableCell>
      <TableCell>
        <Label
          options={modeOptions}
          currentValue={product.mode || ProductMode.REVENUE_OPTIMIZED}
          onOptionChanged={handleModeChange}
        />
      </TableCell>

    </TableRow >
  );
};

export default memo(ProductRow); 